import { useEffect, useState } from "react";
import { X, Sparkles, Loader2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  TextInput,
  TextArea,
  FieldLabel,
  PrimaryButton,
  SecondaryButton,
  Overline,
} from "@/components/ui-kit";

const STEPS = [
  "interview.step1",
  "interview.step2",
  "interview.step3",
  "interview.step4",
  "interview.step5",
  "interview.step6",
];

/**
 * Creative auto-generation wizard.
 * 5 basic inputs (name, industry, target market, location, initial budget) +
 * optional extra notes -> POST /api/ai/generate-plan with mode="creative".
 * Returns a fully-populated project via onCreated(project).
 */
export default function CreativeWizard({ onClose, onCreated }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    industry: "",
    target_market: "",
    location: "",
    initial_budget: "",
    extra_notes: "",
  });
  const [generating, setGenerating] = useState(false);
  const [genStep, setGenStep] = useState(0);

  useEffect(() => {
    if (!generating) return;
    const id = setInterval(() => setGenStep((s) => (s + 1) % STEPS.length), 4500);
    return () => clearInterval(id);
  }, [generating]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const canGenerate = (form.name || form.industry).trim().length > 0;

  async function generate(e) {
    if (e) e.preventDefault();
    if (!canGenerate) return;
    setGenerating(true);
    try {
      const startRes = await api.post("/ai/generate-plan", {
        mode: "creative",
        language: lang,
        name: form.name.trim() || undefined,
        industry: form.industry.trim() || undefined,
        target_market: form.target_market.trim() || undefined,
        location: form.location.trim() || undefined,
        initial_budget: form.initial_budget ? parseFloat(form.initial_budget) : undefined,
        extra_notes: form.extra_notes.trim() || undefined,
      });
      const jobId = startRes.data.job_id;
      const maxAttempts = 60;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const poll = await api.get(`/ai/generate-plan/jobs/${jobId}`);
        if (poll.data.status === "done" && poll.data.project) {
          onCreated(poll.data.project);
          return;
        }
        if (poll.data.status === "failed") {
          throw new Error(poll.data.error || "Generation failed");
        }
      }
      throw new Error(lang === "es" ? "Tiempo de espera agotado" : "Generation timeout");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setGenerating(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-[#1A1A1A]/50 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="creative-wizard-overlay"
    >
      <div
        className="bg-[#F9F8F6] w-full max-w-2xl rounded-sm shadow-2xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-8 py-5 border-b border-[#E8E6E1] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-[#B89154]" strokeWidth={1.5} />
            <Overline>{t("creative.title")}</Overline>
          </div>
          {!generating && (
            <button
              onClick={onClose}
              className="text-[#8A8886] hover:text-[#1A1A1A] transition-colors"
              data-testid="creative-close-btn"
            >
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-8">
          {generating ? (
            <div className="text-center py-8 space-y-6" data-testid="creative-generating">
              <Loader2 className="h-10 w-10 text-[#B89154] mx-auto animate-spin" strokeWidth={1.2} />
              <h2
                className="text-3xl tracking-tight text-[#1A1A1A]"
                style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
              >
                {t("interview.generating_title")}
              </h2>
              <p className="text-sm text-[#5C5B5A] max-w-md mx-auto">
                {t("interview.generating_subtitle")}
              </p>
              <div className="border-t border-[#E8E6E1] pt-6 max-w-md mx-auto space-y-1.5">
                {STEPS.map((stepKey, i) => (
                  <div
                    key={stepKey}
                    className={`flex items-center gap-3 text-sm transition-all ${
                      i < genStep ? "text-[#2563EB]" : i === genStep ? "text-[#1A1A1A] font-medium" : "text-[#8A8886]"
                    }`}
                  >
                    <span className="font-mono text-xs w-6" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span>{i < genStep ? "✓" : i === genStep ? "›" : "·"}</span>
                    <span>{t(stepKey)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={generate} data-testid="creative-form">
              <div>
                <h2
                  className="text-3xl tracking-tight text-[#1A1A1A]"
                  style={{ fontFamily: '"Playfair Display", serif', fontWeight: 700 }}
                >
                  {t("creative.title")}
                </h2>
                <p className="mt-2 text-sm text-[#5C5B5A]">{t("creative.subtitle")}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <FieldLabel>{t("creative.name")}</FieldLabel>
                  <TextInput value={form.name} onChange={(v) => set("name", v)} testId="creative-name" />
                </div>
                <div>
                  <FieldLabel>{t("creative.industry")}</FieldLabel>
                  <TextInput
                    value={form.industry}
                    onChange={(v) => set("industry", v)}
                    placeholder={lang === "es" ? "Ej: SaaS B2B, restauración, ecommerce..." : "e.g. B2B SaaS, restaurants, ecommerce..."}
                    testId="creative-industry"
                  />
                </div>
                <div>
                  <FieldLabel>{t("creative.target")}</FieldLabel>
                  <TextInput
                    value={form.target_market}
                    onChange={(v) => set("target_market", v)}
                    placeholder={lang === "es" ? "Ej: PYMES en España, millennials urbanos..." : "e.g. SMBs in Spain, urban millennials..."}
                    testId="creative-target"
                  />
                </div>
                <div>
                  <FieldLabel>{t("creative.location")}</FieldLabel>
                  <TextInput
                    value={form.location}
                    onChange={(v) => set("location", v)}
                    placeholder={lang === "es" ? "Ej: Madrid, España" : "e.g. Madrid, Spain"}
                    testId="creative-location"
                  />
                </div>
                <div className="md:col-span-2">
                  <FieldLabel>{t("creative.budget")}</FieldLabel>
                  <TextInput
                    type="number"
                    value={form.initial_budget}
                    onChange={(v) => set("initial_budget", v)}
                    placeholder="50000"
                    testId="creative-budget"
                  />
                </div>
                <div className="md:col-span-2">
                  <FieldLabel>{t("creative.notes")}</FieldLabel>
                  <TextArea
                    value={form.extra_notes}
                    onChange={(v) => set("extra_notes", v)}
                    rows={3}
                    placeholder={lang === "es" ? "Cualquier matiz, restricción o idea adicional..." : "Any nuance, constraint or extra idea..."}
                    testId="creative-notes"
                  />
                </div>
              </div>
            </form>
          )}
        </div>

        {!generating && (
          <div className="px-8 py-5 border-t border-[#E8E6E1] flex items-center justify-end gap-3">
            <SecondaryButton onClick={onClose} testId="creative-cancel-btn">
              {t("modal.cancel")}
            </SecondaryButton>
            <PrimaryButton onClick={generate} disabled={!canGenerate} testId="creative-generate-btn">
              <Sparkles className="h-4 w-4" strokeWidth={1.5} />
              {t("creative.generate")}
            </PrimaryButton>
          </div>
        )}
      </div>
    </div>
  );
}
